import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Accordion, AccordionDetails, AccordionSummary, Box, FormGroup, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import type { SelectionDataPipeline } from "@/schemas/routing/selection-pipeline";
import { pipelineCategories } from "@/utils/selection-pipeline";

import { getFilterSteps, getRuleSteps, getSortSteps, ruleNames, sortListByConfiguration } from "../pipeline-steps";

import { OrderedCheckbox } from "./ordered-checkbox";
import { UnorderedCheckbox } from "./unordered-checkbox";

export type AccordionPanel = "filters" | "sort" | "rules";

export interface SelectionPipelineConfigurationProps {
	readonly pipeline: SelectionDataPipeline;
	readonly onChange: (pipeline: SelectionDataPipeline) => void;
	readonly expandedPanel: AccordionPanel | false;
	readonly onAccordionChange: (panel: AccordionPanel) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
}

export const SelectionPipelineConfiguration: React.FC<SelectionPipelineConfigurationProps> = ({
	pipeline,
	onChange,
	expandedPanel,
	onAccordionChange,
}) => {
	const intl = useIntl();

	const updateSelection = (
		checked: boolean,
		category: keyof SelectionDataPipeline,
		name: string,
		configuration?: Record<string, boolean | string> | null,
	): void => {
		onChange(buildUpdatedPipeline(pipeline, checked, category, name, configuration));
	};

	const buildUpdatedPipeline = (
		base: SelectionDataPipeline,
		checked: boolean,
		category: keyof SelectionDataPipeline,
		name: string,
		configuration?: Record<string, boolean | string> | null,
	): SelectionDataPipeline => {
		const categorySteps = base[category];
		if (!Array.isArray(categorySteps)) return base;

		const updatedSteps = checked
			? [...categorySteps, { name, configuration: configuration ?? null }]
			: categorySteps.filter((step) => step.name !== name);

		return { ...base, [category]: updatedSteps };
	};

	const unSelectOddTilingPair = (checked: boolean, currentPipeline: SelectionDataPipeline): SelectionDataPipeline => {
		if (checked) return currentPipeline;
		return buildUpdatedPipeline(currentPipeline, false, "rulesSteps", ruleNames.produceOddTilingPair);
	};

	const accordionSx = (panel: AccordionPanel) =>
		expandedPanel === panel
			? {
					display: "flex",
					flexDirection: "column" as const,
					flex: 1,
					minHeight: 0,
					"& .MuiAccordion-region": { display: "flex", flexDirection: "column", flex: 1, minHeight: 0 },
				}
			: { flex: "0 0 auto" };

	return (
		<Box display="flex" flexDirection="column" flex={1} minHeight={0}>
			<Accordion
				expanded={expandedPanel === "filters"}
				onChange={onAccordionChange("filters")}
				disableGutters
				sx={accordionSx("filters")}
			>
				<AccordionSummary
					sx={{ backgroundColor: (theme) => theme.palette.background.default }}
					expandIcon={<ExpandMoreIcon />}
					data-testid="production-groups-configuration-modal__accordion__filters"
				>
					<FormattedMessage id="Filters" defaultMessage="Filters" />
				</AccordionSummary>
				<AccordionDetails sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
					<FormGroup>
						{sortListByConfiguration(getFilterSteps(intl), pipeline.filterSteps).map((s) => (
							<UnorderedCheckbox
								key={s.name}
								label={s.label}
								category={pipelineCategories.filters}
								name={s.name}
								pipeline={pipeline}
								pendoId={`production-groups-configuration-modal__checkbox__filters-${s.name?.toLowerCase()?.replace(/\s+/g, "-")}`}
								handleClick={(e) => updateSelection(e.target.checked, "filterSteps", s.name, s.configuration)}
							/>
						))}
					</FormGroup>
				</AccordionDetails>
			</Accordion>
			<Accordion
				expanded={expandedPanel === "sort"}
				onChange={onAccordionChange("sort")}
				disableGutters
				sx={accordionSx("sort")}
			>
				<AccordionSummary
					sx={{ backgroundColor: (theme) => theme.palette.background.default }}
					expandIcon={<ExpandMoreIcon />}
					data-testid="production-groups-configuration-modal__accordion__sort"
				>
					<FormattedMessage id="Sort By" defaultMessage="Sort By" />
					{expandedPanel === "sort" && (
						<Typography variant="body2" fontStyle="italic" sx={{ ml: 2 }}>
							<FormattedMessage
								id="Note Sort in order of greatest importance"
								defaultMessage="Note: Sort in order of greatest importance."
							/>
						</Typography>
					)}
				</AccordionSummary>
				<AccordionDetails sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
					<FormGroup>
						{sortListByConfiguration(getSortSteps(intl), pipeline.sortSteps).map((s) => (
							<OrderedCheckbox
								key={s.name}
								label={s.label}
								category={pipelineCategories.sorts}
								name={s.name}
								conflictsWith={s.conflictsWith}
								updatePipeline={(updatedPipeline) => onChange(updatedPipeline)}
								pipeline={pipeline}
								pendoId={`production-groups-configuration-modal__checkbox__sort-${s.name?.toLowerCase()?.replace(/\s+/g, "-")}`}
								handleClick={(e) => updateSelection(e.target.checked, "sortSteps", s.name, s.configuration)}
							/>
						))}
					</FormGroup>
				</AccordionDetails>
			</Accordion>
			<Accordion
				expanded={expandedPanel === "rules"}
				onChange={onAccordionChange("rules")}
				disableGutters
				sx={accordionSx("rules")}
			>
				<AccordionSummary
					sx={{ backgroundColor: (theme) => theme.palette.background.default }}
					expandIcon={<ExpandMoreIcon />}
					data-testid="production-groups-configuration-modal__accordion__rules"
				>
					<FormattedMessage id="Rules" defaultMessage="Rules" />
				</AccordionSummary>
				<AccordionDetails sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
					<FormGroup>
						{sortListByConfiguration(
							getRuleSteps({ intl, onTilingChanged: unSelectOddTilingPair }),
							pipeline.rulesSteps,
						).map((s) => (
							<UnorderedCheckbox
								key={s.name}
								label={s.label}
								category={pipelineCategories.rules}
								name={s.name}
								pipeline={pipeline}
								conflictsWith={s.conflictsWith}
								preRequesites={s.preRequesites}
								pendoId={`production-groups-configuration-modal__checkbox__rules-${s.name?.toLowerCase()?.replace(/\s+/g, "-")}`}
								handleClick={(e) => {
									const checked = e.target.checked;
									const updatedPipeline = buildUpdatedPipeline(
										pipeline,
										checked,
										"rulesSteps",
										s.name,
										s.configuration,
									);
									onChange(s.postSelection?.(checked, updatedPipeline) ?? updatedPipeline);
								}}
							/>
						))}
					</FormGroup>
				</AccordionDetails>
			</Accordion>
		</Box>
	);
};
